import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(form);
      toast.success("Account created. You can book now.");
      navigate("/book", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const field = (id, label, type = "text", extra = {}) => (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        required
        value={form[id]}
        data-testid={`register-${id}-input`}
        onChange={(e) => setForm({ ...form, [id]: e.target.value })}
        className="rounded-xl h-11 focus:ring-2 focus:ring-sky-500"
        {...extra}
      />
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="register-page">
      <h1 className="text-3xl sm:text-4xl tracking-tight text-slate-900">Create Account</h1>
      <p className="mt-3 text-sm text-slate-600">Takes under a minute. No payment details needed.</p>

      <form onSubmit={submit} className="mt-10 space-y-5 bg-white border border-slate-100 rounded-2xl p-8">
        {field("name", "Full name")}
        {field("email", "Email", "email")}
        {field("phone", "Mobile number", "tel")}
        {field("password", "Password", "password", { minLength: 6 })}

        {error && (
          <p className="text-sm text-red-600" data-testid="register-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid="register-submit-btn"
          className="w-full rounded-full h-11 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
        >
          {busy ? "Creating account…" : "Create Account"}
        </Button>

        <div className="relative py-2 text-center">
          <span className="text-xs uppercase tracking-widest text-slate-400 bg-white px-3 relative z-10">or</span>
          <span className="absolute left-0 right-0 top-1/2 h-px bg-slate-200" />
        </div>

        <GoogleSignInButton label="Sign up with Google" />

        <p className="text-sm text-slate-600 text-center pt-2">
          Already registered?{" "}
          <Link to="/signin" className="text-sky-600 hover:underline" data-testid="register-signin-link">
            Sign In
          </Link>
        </p>
      </form>
    </div>
  );
}
